import type { RunningRun } from "./fixtures";
import { useRunningNow } from "./useAgentsUi";

/**
 * "Running now" — the live in-flight runs strip at the top of the Agents tab (design: runs
 * are a first-class noun). Full-width rows: a running dot + title + progress sub-line on the
 * left, a state pill on the right. Live (in-flight agent_runs) when VITE_AGENTS_LIVE is on,
 * fixtures otherwise. The section is ALWAYS rendered for a consistent layout — when nothing
 * is in flight it shows a muted "Nothing running right now" empty state rather than vanishing.
 *
 * A row that carries an `agentId` (live data) is clickable: it opens the SAME consolidated
 * run/agent detail a routine card opens (via `onOpen`) — no dead tiles. Fixture rows have no
 * agentId, so they render as static rows (the fixture demo has no live agent to open).
 */
export function RunningNow({
  onOpen,
}: {
  /** Open the agent's consolidated card/detail — wired to the routine-card expand. */
  onOpen?: (agentId: string) => void;
}) {
  const { data: runs = [] } = useRunningNow();

  return (
    <section className="agui-running" aria-label="Running now">
      <div className="agui-block__label">Running now</div>
      {runs.length === 0 ? (
        <div className="agui-running__empty">Nothing running right now</div>
      ) : (
        <div className="agui-running__list">
          {runs.map((r) => (
            <RunningRowItem key={r.id} run={r} onOpen={onOpen} />
          ))}
        </div>
      )}
    </section>
  );
}

function RunningRowItem({
  run: r,
  onOpen,
}: {
  run: RunningRun;
  onOpen?: (agentId: string) => void;
}) {
  const clickable = Boolean(r.agentId && onOpen);
  const open = () => {
    if (r.agentId && onOpen) onOpen(r.agentId);
  };
  return (
    <div
      className={`agui-runningrow${clickable ? " is-clickable" : ""}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? open : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                open();
              }
            }
          : undefined
      }
    >
      <span className="agui-runningrow__dot" aria-hidden="true" />
      <div className="agui-runningrow__id">
        <div className="agui-runningrow__title">{r.title}</div>
        <div className="agui-runningrow__sub">{r.sub}</div>
      </div>
      <span className="agui-runningrow__state">{r.state}</span>
    </div>
  );
}
